import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageCircle, UserCog, Search, RefreshCw, Send, Paperclip, Check, CheckCheck, Reply, X,
  Download, FileText, ArrowLeft, Plus, Lock, MapPin, User, Image as ImageIcon, Trash2, Users,
} from 'lucide-react';
import api from '../../services/api';

const BASE = '/whatsapp-web';
const msgErro = (e) => e.response?.data?.error || e.message;
const hora = (ms) => (ms ? new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '');
function quando(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const hoje = new Date();
  if (d.toDateString() === hoje.toDateString()) return hora(ms);
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
const dia = (ms) => new Date(ms).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

function lerArquivo(file) {
  return new Promise((ok, erro) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result).replace(/^data:[^,]+,/, ''));
    r.onerror = erro;
    r.readAsDataURL(file);
  });
}

function Aba({ id, atual, set, icon: Icon, children }) {
  return (
    <button onClick={() => set(id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${atual === id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      <Icon size={16} /> {children}
    </button>
  );
}

function Avatar({ foto, nome, grupo, tam = 40 }) {
  const [erro, setErro] = useState(false);
  const ini = (nome || '?').replace(/[^\p{L}\p{N} ]/gu, '').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
  if (foto && !erro) {
    return <img src={foto} alt="" onError={() => setErro(true)} referrerPolicy="no-referrer" className="rounded-full object-cover flex-shrink-0 bg-gray-200" style={{ width: tam, height: tam }} />;
  }
  return (
    <div className="rounded-full bg-gray-300 text-gray-600 flex items-center justify-center flex-shrink-0 text-xs font-semibold" style={{ width: tam, height: tam }}>
      {grupo ? <Users size={tam / 2.2} /> : ini}
    </div>
  );
}

// Mídia vem do backend com o token (não dá pra usar <img src> direto) — baixa como blob.
function useMidia(id, ativo = true) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState(false);
  useEffect(() => {
    if (!ativo || !id) return undefined;
    let vivo = true;
    let obj;
    api.get(`${BASE}/midia/${id}`, { responseType: 'blob' })
      .then((r) => { if (vivo) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => vivo && setErro(true));
    return () => { vivo = false; if (obj) URL.revokeObjectURL(obj); };
  }, [id, ativo]);
  return { url, erro };
}

function Midia({ m }) {
  const [pedido, setPedido] = useState(m.tipo === 'imagem' || m.tipo === 'figurinha' || m.tipo === 'audio');
  const { url, erro } = useMidia(m.id, pedido);
  if (erro) return <div className="text-xs italic text-gray-500">[mídia indisponível]</div>;
  if (m.tipo === 'imagem' || m.tipo === 'figurinha') {
    const cls = m.tipo === 'figurinha' ? 'w-32 h-32 object-contain' : 'max-w-xs max-h-80 rounded';
    return url
      ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className={cls} /></a>
      : <div className="w-48 h-32 bg-black/5 rounded flex items-center justify-center text-gray-400"><ImageIcon size={24} /></div>;
  }
  if (m.tipo === 'audio') return url ? <audio controls src={url} className="w-64 max-w-full" /> : <div className="text-xs text-gray-500">carregando áudio…</div>;
  if (m.tipo === 'video') {
    if (!pedido) return <button onClick={() => setPedido(true)} className="px-3 py-6 w-56 bg-black/5 rounded text-sm text-gray-600">▶ Carregar vídeo{m.midia?.segundos ? ` (${m.midia.segundos}s)` : ''}</button>;
    return url ? <video controls src={url} className="max-w-xs max-h-80 rounded" /> : <div className="text-xs text-gray-500">carregando vídeo…</div>;
  }
  if (m.tipo === 'documento') {
    const baixar = async () => {
      const r = await api.get(`${BASE}/midia/${m.id}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = m.midia?.nome || 'documento';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    };
    return (
      <button onClick={baixar} className="flex items-center gap-2 bg-black/5 rounded px-3 py-2 text-sm text-left max-w-xs">
        <FileText size={20} className="text-gray-500 flex-shrink-0" />
        <span className="truncate flex-1">{m.midia?.nome || 'Documento'}</span>
        <Download size={16} className="text-gray-500 flex-shrink-0" />
      </button>
    );
  }
  return null;
}

function Balao({ m, grupo, onResponder }) {
  const temMidia = ['imagem', 'figurinha', 'audio', 'video', 'documento'].includes(m.tipo);
  return (
    <div className={`group flex ${m.deMim ? 'justify-end' : 'justify-start'}`}>
      <div className={`relative max-w-[80%] md:max-w-[65%] rounded-lg px-2.5 py-1.5 shadow-sm text-sm ${m.deMim ? 'bg-[#d9fdd3]' : 'bg-white'}`}>
        {grupo && !m.deMim && m.autor && <div className="text-xs font-semibold text-green-700 mb-0.5">{m.autor}</div>}
        {m.citada && <div className="border-l-4 border-green-600 bg-black/5 rounded px-2 py-1 mb-1 text-xs text-gray-600 line-clamp-3 whitespace-pre-wrap">{m.citada}</div>}
        {temMidia && <div className="mb-1"><Midia m={m} /></div>}
        {m.tipo === 'localizacao' && (
          <a href={`https://maps.google.com/?q=${m.local?.lat},${m.local?.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-700 underline">
            <MapPin size={14} /> {m.texto}
          </a>
        )}
        {m.tipo === 'contato' && <div className="flex items-center gap-1"><User size={14} /> {m.texto}</div>}
        {m.texto && !['localizacao', 'contato'].includes(m.tipo) && <div className="whitespace-pre-wrap break-words">{m.texto}</div>}
        <div className="flex items-center justify-end gap-1 text-[10px] text-gray-500 mt-0.5">
          {hora(m.em)}
          {m.deMim && (m.status === 'lida' ? <CheckCheck size={14} className="text-sky-500" /> : m.status === 'entregue' ? <CheckCheck size={14} /> : <Check size={14} />)}
        </div>
        {m.reacoes?.length > 0 && <div className="absolute -bottom-3 right-2 bg-white rounded-full shadow px-1 text-xs">{m.reacoes.join('')}</div>}
        <button onClick={() => onResponder(m)} title="Responder" className={`absolute top-1 ${m.deMim ? '-left-7' : '-right-7'} hidden group-hover:block text-gray-400 hover:text-gray-700`}>
          <Reply size={16} />
        </button>
      </div>
    </div>
  );
}

function ListaConversas({ atual, onAbrir }) {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');
  const [naoLidas, setNaoLidas] = useState(false);
  const [limite, setLimite] = useState(80);
  const [novo, setNovo] = useState(null);
  useEffect(() => { const t = setTimeout(() => setTermo(busca), 300); return () => clearTimeout(t); }, [busca]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['wa-conversas', termo, naoLidas, limite],
    queryFn: () => api.get(`${BASE}/conversas`, { params: { busca: termo, limite, naoLidas: naoLidas ? 1 : 0 } }).then((r) => r.data.data),
    refetchInterval: 15000,
    placeholderData: (anterior) => anterior,
  });

  const iniciar = useMutation({
    mutationFn: () => api.post(`${BASE}/conversas/nova`, novo),
    onSuccess: () => { setNovo(null); setTimeout(() => qc.invalidateQueries({ queryKey: ['wa-conversas'] }), 2000); },
    onError: (e) => alert(msgErro(e)),
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar nome ou número" className="pl-8 pr-3 py-2 border rounded-lg text-sm w-full" />
          </div>
          <button title="Nova conversa" onClick={() => setNovo({ numero: '', texto: '' })} className="px-2.5 rounded-lg border text-gray-600 hover:bg-gray-50"><Plus size={16} /></button>
          <button title="Atualizar" onClick={() => qc.invalidateQueries({ queryKey: ['wa-conversas'] })} className="px-2.5 rounded-lg border text-gray-600 hover:bg-gray-50">
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={naoLidas} onChange={(e) => setNaoLidas(e.target.checked)} /> Só não lidas</label>
        {novo && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 space-y-2">
            <input autoFocus value={novo.numero} onChange={(e) => setNovo({ ...novo, numero: e.target.value })} placeholder="Número com DDD (ex.: 44 99999-9999)" className="border rounded px-2 py-1.5 text-sm w-full" />
            <textarea value={novo.texto} onChange={(e) => setNovo({ ...novo, texto: e.target.value })} placeholder="Mensagem" rows={2} className="border rounded px-2 py-1.5 text-sm w-full" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setNovo(null)} className="px-3 py-1 text-sm text-gray-600">Cancelar</button>
              <button onClick={() => iniciar.mutate()} disabled={iniciar.isPending || !novo.numero || !novo.texto.trim()} className="px-3 py-1 rounded bg-green-600 text-white text-sm disabled:opacity-40">Enviar</button>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && <div className="p-4 text-sm text-gray-500">Carregando conversas… (a primeira carga leva uns segundos)</div>}
        {error && <div className="p-4 text-sm text-red-600">{msgErro(error)}</div>}
        {data?.conversas.map((c) => (
          <button key={c.jid} onClick={() => onAbrir(c)} className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 text-left hover:bg-gray-50 ${atual?.jid === c.jid ? 'bg-gray-100' : ''}`}>
            <Avatar foto={c.foto} nome={c.nome} grupo={c.grupo} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-medium text-sm text-gray-900 truncate flex-1">{c.nome}</span>
                <span className={`text-[11px] flex-shrink-0 ${c.naoLidas ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>{quando(c.ultima?.em)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 truncate flex-1">{c.ultima?.deMim ? 'Você: ' : ''}{c.ultima?.texto}</span>
                {c.naoLidas > 0 && <span className="bg-green-600 text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center flex-shrink-0">{c.naoLidas > 99 ? '99+' : c.naoLidas}</span>}
              </div>
            </div>
          </button>
        ))}
        {data && data.conversas.length < data.total && (
          <button onClick={() => setLimite((l) => l + 80)} className="w-full py-3 text-sm text-green-700 hover:bg-gray-50">Mostrar mais ({data.total - data.conversas.length})</button>
        )}
        {data && !data.total && <div className="p-4 text-sm text-gray-500">Nenhuma conversa.</div>}
      </div>
    </div>
  );
}

function Conversa({ conv, onVoltar }) {
  const qc = useQueryClient();
  const [pagina, setPagina] = useState(1);
  const [antigas, setAntigas] = useState([]);
  const [temMaisAntigas, setTemMaisAntigas] = useState(false);
  const [texto, setTexto] = useState('');
  const [citar, setCitar] = useState(null);
  const [anexo, setAnexo] = useState(null);
  const fimRef = useRef(null);
  const caixaRef = useRef(null);
  const arquivoRef = useRef(null);
  const ultimoIdRef = useRef(null);
  const lidoRef = useRef(false);
  const jidUrl = encodeURIComponent(conv.jid);

  useEffect(() => {
    setPagina(1); setAntigas([]); setTexto(''); setCitar(null); setAnexo(null);
    ultimoIdRef.current = null; lidoRef.current = false;
  }, [conv.jid]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['wa-msgs', conv.jid],
    queryFn: () => api.get(`${BASE}/conversas/${jidUrl}/mensagens`).then((r) => r.data.data),
    refetchInterval: 6000,
  });
  useEffect(() => { if (data && pagina === 1) setTemMaisAntigas(data.temMais); }, [data, pagina]);

  const mensagens = useMemo(() => {
    const vistos = new Set();
    return [...antigas, ...(data?.mensagens || [])].filter((m) => (vistos.has(m.id) ? false : vistos.add(m.id))).sort((a, b) => (a.em || 0) - (b.em || 0));
  }, [antigas, data]);

  // rola pro fim quando chega mensagem nova (não quando carrega as antigas)
  useEffect(() => {
    const ultimo = mensagens[mensagens.length - 1]?.id;
    if (ultimo && ultimo !== ultimoIdRef.current) {
      ultimoIdRef.current = ultimo;
      fimRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [mensagens]);

  // abriu conversa com não lidas → marca como lida no celular também
  useEffect(() => {
    if (!data || lidoRef.current || !conv.naoLidas) return;
    lidoRef.current = true;
    const ids = data.mensagens.filter((m) => !m.deMim).slice(-30).map((m) => m.id);
    if (ids.length) api.post(`${BASE}/conversas/${jidUrl}/lido`, { ids }).then(() => qc.invalidateQueries({ queryKey: ['wa-conversas'] })).catch(() => {});
  }, [data, conv.naoLidas, jidUrl, qc]);

  const carregarAntigas = async () => {
    const prox = pagina + 1;
    const altura = caixaRef.current?.scrollHeight || 0;
    const r = await api.get(`${BASE}/conversas/${jidUrl}/mensagens`, { params: { pagina: prox } });
    setAntigas((a) => [...r.data.data.mensagens, ...a]);
    setTemMaisAntigas(r.data.data.temMais);
    setPagina(prox);
    requestAnimationFrame(() => { if (caixaRef.current) caixaRef.current.scrollTop = caixaRef.current.scrollHeight - altura; });
  };

  const enviar = useMutation({
    mutationFn: async () => {
      const body = { texto, citar: citar?.id };
      if (anexo) body.arquivo = { base64: await lerArquivo(anexo), mimetype: anexo.type, nome: anexo.name };
      return api.post(`${BASE}/conversas/${jidUrl}/enviar`, body);
    },
    onSuccess: () => {
      setTexto(''); setCitar(null); setAnexo(null);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['wa-msgs', conv.jid] }), 800);
      qc.invalidateQueries({ queryKey: ['wa-conversas'] });
    },
    onError: (e) => alert(`Não enviou: ${msgErro(e)}`),
  });
  const podeEnviar = !enviar.isPending && (anexo || texto.trim());

  let diaAnterior = null;
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-3 py-2 border-b bg-gray-50">
        <button onClick={onVoltar} className="md:hidden text-gray-600"><ArrowLeft size={20} /></button>
        <Avatar foto={conv.foto} nome={conv.nome} grupo={conv.grupo} />
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{conv.nome}</div>
          <div className="text-xs text-gray-500">{conv.grupo ? 'Grupo' : conv.numero ? `+${conv.numero}` : ''}</div>
        </div>
      </div>

      <div ref={caixaRef} className="flex-1 overflow-y-auto px-3 md:px-8 py-3 space-y-1.5 bg-[#efeae2]">
        {temMaisAntigas && (
          <div className="text-center"><button onClick={carregarAntigas} className="text-xs bg-white rounded-full shadow px-3 py-1 text-gray-600">Carregar mensagens anteriores</button></div>
        )}
        {isLoading && <div className="text-center text-sm text-gray-500">Carregando…</div>}
        {error && <div className="text-center text-sm text-red-600">{msgErro(error)}</div>}
        {mensagens.map((m) => {
          const d = m.em ? new Date(m.em).toDateString() : null;
          const separador = d && d !== diaAnterior;
          diaAnterior = d || diaAnterior;
          return (
            <React.Fragment key={m.id}>
              {separador && <div className="text-center py-1"><span className="text-[11px] bg-white/90 rounded px-2 py-0.5 text-gray-600 shadow-sm">{dia(m.em)}</span></div>}
              <div className={m.reacoes?.length ? 'pb-3' : ''}><Balao m={m} grupo={conv.grupo} onResponder={setCitar} /></div>
            </React.Fragment>
          );
        })}
        {data && !mensagens.length && <div className="text-center text-sm text-gray-500 py-6">Sem mensagens salvas desta conversa.</div>}
        <div ref={fimRef} />
      </div>

      <div className="border-t bg-gray-50 p-2 space-y-2">
        {citar && (
          <div className="flex items-start gap-2 bg-white border-l-4 border-green-600 rounded px-2 py-1 text-xs text-gray-600">
            <span className="flex-1 line-clamp-2">{citar.texto || `[${citar.tipo}]`}</span>
            <button onClick={() => setCitar(null)}><X size={14} /></button>
          </div>
        )}
        {anexo && (
          <div className="flex items-center gap-2 bg-white rounded px-2 py-1 text-xs text-gray-700">
            <Paperclip size={14} /> <span className="flex-1 truncate">{anexo.name} ({Math.round(anexo.size / 1024)} KB)</span>
            <button onClick={() => setAnexo(null)}><X size={14} /></button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={arquivoRef} type="file" className="hidden" onChange={(e) => { setAnexo(e.target.files?.[0] || null); e.target.value = ''; }} />
          <button title="Anexar" onClick={() => arquivoRef.current?.click()} className="p-2 text-gray-500 hover:text-gray-700"><Paperclip size={20} /></button>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (podeEnviar) enviar.mutate(); } }}
            rows={Math.min(5, texto.split('\n').length)}
            placeholder={anexo ? 'Legenda (opcional)' : 'Mensagem'}
            className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none"
          />
          <button onClick={() => enviar.mutate()} disabled={!podeEnviar} className="p-2.5 rounded-full bg-green-600 text-white disabled:opacity-40"><Send size={18} /></button>
        </div>
      </div>
    </div>
  );
}

function Conversas() {
  const [atual, setAtual] = useState(null);
  return (
    <div className="flex h-full bg-white rounded-lg border overflow-hidden">
      <div className={`${atual ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r flex-shrink-0`}>
        <ListaConversas atual={atual} onAbrir={setAtual} />
      </div>
      <div className={`${atual ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0`}>
        {atual
          ? <Conversa conv={atual} onVoltar={() => setAtual(null)} />
          : <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50"><MessageCircle size={48} /><p className="mt-2 text-sm">Escolha uma conversa</p></div>}
      </div>
    </div>
  );
}

const OPCOES_PRIVACIDADE = {
  last: ['Visto por último', [['all', 'Todos'], ['contacts', 'Meus contatos'], ['contact_blacklist', 'Meus contatos, exceto…'], ['none', 'Ninguém']]],
  online: ['Online', [['all', 'Todos'], ['match_last_seen', 'Igual ao visto por último']]],
  profile: ['Foto do perfil', [['all', 'Todos'], ['contacts', 'Meus contatos'], ['contact_blacklist', 'Meus contatos, exceto…'], ['none', 'Ninguém']]],
  status: ['Recado', [['all', 'Todos'], ['contacts', 'Meus contatos'], ['contact_blacklist', 'Meus contatos, exceto…'], ['none', 'Ninguém']]],
  readreceipts: ['Confirmação de leitura', [['all', 'Ligada'], ['none', 'Desligada']]],
  groupadd: ['Quem pode me adicionar em grupos', [['all', 'Todos'], ['contacts', 'Meus contatos'], ['contact_blacklist', 'Meus contatos, exceto…']]],
};

function Perfil() {
  const qc = useQueryClient();
  const { data: p, isLoading, error } = useQuery({ queryKey: ['wa-perfil'], queryFn: () => api.get(`${BASE}/perfil`).then((r) => r.data.data) });
  const [nome, setNome] = useState('');
  const [recado, setRecado] = useState('');
  const [priv, setPriv] = useState({});
  const [aviso, setAviso] = useState('');
  const fotoRef = useRef(null);
  useEffect(() => { if (p) { setNome(p.nome || ''); setRecado(p.recado || ''); setPriv(p.privacidade || {}); } }, [p]);

  const useAcao = (fn, msg) => useMutation({
    mutationFn: fn,
    onSuccess: () => { setAviso(msg); setTimeout(() => setAviso(''), 4000); setTimeout(() => qc.invalidateQueries({ queryKey: ['wa-perfil'] }), 1500); },
    onError: (e) => alert(msgErro(e)),
  });
  const salvarNome = useAcao(() => api.put(`${BASE}/perfil/nome`, { nome }), 'Nome atualizado');
  const salvarRecado = useAcao(() => api.put(`${BASE}/perfil/recado`, { recado }), 'Recado atualizado');
  const salvarFoto = useAcao(async (file) => api.put(`${BASE}/perfil/foto`, { imagem: await lerArquivo(file) }), 'Foto atualizada');
  const removerFoto = useAcao(() => api.delete(`${BASE}/perfil/foto`), 'Foto removida');
  const salvarPriv = useAcao(() => api.put(`${BASE}/perfil/privacidade`, priv), 'Privacidade atualizada (o WhatsApp reconecta em alguns segundos)');

  if (isLoading) return <div className="text-sm text-gray-500">Carregando perfil…</div>;
  if (error) return <div className="text-sm text-red-600">{msgErro(error)}</div>;
  const mudouPriv = p?.privacidade && Object.keys(OPCOES_PRIVACIDADE).some((k) => priv[k] !== p.privacidade[k]);

  return (
    <div className="max-w-3xl space-y-4">
      {aviso && <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-3 py-2">{aviso}</div>}
      <div className="bg-white rounded-lg border p-4 flex flex-col sm:flex-row gap-5">
        <div className="flex flex-col items-center gap-2">
          <Avatar foto={p.foto} nome={p.nome} tam={128} />
          <input ref={fotoRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f && window.confirm('Trocar a foto do seu WhatsApp por esta imagem?')) salvarFoto.mutate(f); }} />
          <div className="flex gap-2">
            <button onClick={() => fotoRef.current?.click()} disabled={salvarFoto.isPending} className="px-3 py-1.5 rounded-lg border text-sm text-gray-700 disabled:opacity-40">{salvarFoto.isPending ? 'Enviando…' : 'Trocar foto'}</button>
            {p.foto && <button title="Remover foto" onClick={() => window.confirm('Remover a foto do seu WhatsApp?') && removerFoto.mutate()} className="px-2 py-1.5 rounded-lg border text-red-600"><Trash2 size={15} /></button>}
          </div>
          <p className="text-[11px] text-gray-400 text-center">Quadrada, mín. 640×640</p>
        </div>
        <div className="flex-1 space-y-4">
          <div className="text-sm text-gray-500">
            {p.conectado ? <span className="text-green-700">● Conectado</span> : <span className="text-red-600">● Desconectado ({p.estado})</span>}
            {' · '}+{p.numero} · instância <code>{p.instancia}</code>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Nome</label>
            <div className="flex gap-2">
              <input value={nome} maxLength={25} onChange={(e) => setNome(e.target.value)} className="border rounded-lg px-3 py-2 text-sm flex-1" />
              <button onClick={() => salvarNome.mutate()} disabled={salvarNome.isPending || !nome.trim() || nome === p.nome} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-40">Salvar</button>
            </div>
            <div className="text-[11px] text-gray-400 text-right">{nome.length}/25</div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Recado</label>
            <div className="flex gap-2">
              <input value={recado} maxLength={139} onChange={(e) => setRecado(e.target.value)} placeholder="Ex.: Disponível" className="border rounded-lg px-3 py-2 text-sm flex-1" />
              <button onClick={() => salvarRecado.mutate()} disabled={salvarRecado.isPending || recado === p.recado} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-40">Salvar</button>
            </div>
            <div className="text-[11px] text-gray-400 text-right">{recado.length}/139</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 space-y-3">
        <h3 className="font-medium text-gray-800 flex items-center gap-2"><Lock size={16} /> Privacidade</h3>
        {p.privacidade ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(OPCOES_PRIVACIDADE).map(([k, [rotulo, opcoes]]) => (
                <label key={k} className="text-sm">
                  <span className="text-xs font-medium text-gray-600">{rotulo}</span>
                  <select value={priv[k] || ''} onChange={(e) => setPriv({ ...priv, [k]: e.target.value })} className="border rounded-lg px-3 py-2 text-sm w-full">
                    {opcoes.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3">
              <span className="text-[11px] text-gray-400">Ao salvar, a Evolution reconecta o WhatsApp (alguns segundos).</span>
              <button onClick={() => salvarPriv.mutate()} disabled={!mudouPriv || salvarPriv.isPending} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-40">Salvar privacidade</button>
            </div>
          </>
        ) : <p className="text-sm text-gray-500">Não foi possível ler a privacidade.</p>}
      </div>

      {p.negocio && (
        <div className="bg-white rounded-lg border p-4 space-y-2">
          <h3 className="font-medium text-gray-800">Perfil comercial</h3>
          <dl className="grid sm:grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-sm">
            {[['Descrição', p.negocio.descricao], ['Categoria', p.negocio.categoria], ['E-mail', p.negocio.email], ['Site', p.negocio.site], ['Endereço', p.negocio.endereco]].map(([k, v]) => (
              <React.Fragment key={k}><dt className="text-gray-500">{k}</dt><dd className="text-gray-800">{v || '—'}</dd></React.Fragment>
            ))}
          </dl>
          <p className="text-xs text-gray-500">Esses campos só mudam pelo app WhatsApp Business no celular (Configurações › Ferramentas comerciais › Perfil comercial).</p>
        </div>
      )}
    </div>
  );
}

export default function WhatsAppWeb() {
  const [aba, setAba] = useState('conversas');
  return (
    <div className="flex flex-col h-full min-h-[520px] gap-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2"><MessageCircle className="text-green-600" /> WhatsApp Web</h1>
        <span className="text-xs text-gray-400">visível só para você</span>
      </div>
      <div className="flex border-b">
        <Aba id="conversas" atual={aba} set={setAba} icon={MessageCircle}>Conversas</Aba>
        <Aba id="perfil" atual={aba} set={setAba} icon={UserCog}>Perfil</Aba>
      </div>
      <div className={aba === 'conversas' ? 'flex-1 min-h-0' : 'hidden'}><Conversas /></div>
      {aba === 'perfil' && <Perfil />}
    </div>
  );
}
